import { create } from 'zustand';
import { isAccessRevocationDuplicate } from '@/lib/access-revocation';

export type AccessRevocationKind = 'project' | 'org';

export type AccessRevocationRedirectTarget = 'workspace' | 'project';

export interface AccessRevocationPayload {
  kind: AccessRevocationKind;
  title?: string;
  description?: string;
  orgId?: string;
  projectId?: string;
  projectName?: string;
  affectedProjectIds?: string[];
  /** 倒计时结束后的跳转目标，默认工作台 */
  redirectTarget?: AccessRevocationRedirectTarget;
  /** redirectTarget=project 时跳转到 /project/:returnProjectId */
  returnProjectId?: string;
  /** 优先于默认工作台 / 编辑页的显式返回路径 */
  returnPath?: string;
}

export interface AccessRevocationDialogState {
  open: boolean;
  kind: AccessRevocationKind | null;
  title: string;
  description: string;
  orgId?: string;
  projectId?: string;
  projectName?: string;
  affectedProjectIds: string[];
  redirectTarget: AccessRevocationRedirectTarget;
  returnProjectId?: string;
  returnPath?: string;
}

interface AccessRevocationStore {
  dialog: AccessRevocationDialogState;
  show: (payload: AccessRevocationPayload) => void;
  clear: () => void;
}

const initialDialog: AccessRevocationDialogState = {
  open: false,
  kind: null,
  title: '',
  description: '',
  affectedProjectIds: [],
  redirectTarget: 'workspace',
};

function resolveTitle(payload: AccessRevocationPayload): string {
  if (payload.title) return payload.title;
  return payload.kind === 'org' ? '组织访问权限已变更' : '项目访问权限已变更';
}

function resolveDescription(payload: AccessRevocationPayload): string {
  if (payload.description) return payload.description;
  if (payload.kind === 'org') {
    return '你已不在该组织中，当前页面将自动返回工作台。';
  }
  const projectName = payload.projectName ? `「${payload.projectName}」` : '该项目';
  return `你已不在项目${projectName}中，当前页面将自动返回工作台。`;
}

export const useAccessRevocationStore = create<AccessRevocationStore>((set, get) => ({
  dialog: initialDialog,
  show: (payload) => {
    const current = get().dialog;
    if (isAccessRevocationDuplicate(current, payload)) return;
    if (current.open && current.kind === 'org' && payload.kind === 'project') return;

    set({
      dialog: {
        open: true,
        kind: payload.kind,
        title: resolveTitle(payload),
        description: resolveDescription(payload),
        orgId: payload.orgId,
        projectId: payload.projectId,
        projectName: payload.projectName,
        affectedProjectIds: payload.affectedProjectIds ?? [],
        redirectTarget: payload.redirectTarget ?? 'workspace',
        returnProjectId: payload.returnProjectId,
        returnPath: payload.returnPath,
      },
    });
  },
  clear: () => set({ dialog: initialDialog }),
}));
